import axiosInstance from '@/services/axiosInstance';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

export type ApplicationErrorSeverity = 'CRITICAL' | 'ERROR' | 'WARN' | 'INFO';
export type ApplicationErrorFilter = ApplicationErrorSeverity | 'ALL';

export interface ApplicationErrorListItem {
  errorId: string;
  createdAt: string;
  severity: ApplicationErrorSeverity;
  applicationName: string;
  serviceName: string;
  errorType: string;
  errorMessage: string;
}

export interface ApplicationErrorDetails extends ApplicationErrorListItem {
  environment?: string;
  stackTrace?: string;
  rootCause?: string;
  requestId?: string;
  correlationId?: string;
  userId?: string;
  httpMethod?: string;
  endpoint?: string;
  httpStatusCode?: number;
  payload?: unknown;
  metadata?: unknown;
}

export interface ApplicationErrorListQueryParams {
  severity?: ApplicationErrorSeverity;
  search?: string;
  pageNo?: number;
  pageSize?: number;
}

export interface ApplicationErrorListResponse {
  data: ApplicationErrorListItem[];
  totalRows: number;
  totalPages: number;
  currentPage: number;
  totalLogs: number;
  totalCriticalLogs: number;
  totalErrorLogs: number;
  totalInfoLogs: number;
}

export interface ApplicationErrorSummaryResponse {
  totalLogs: number;
  criticalLogs: number;
  errorLogs: number;
  warnLogs: number;
}

type ApplicationErrorListResponseEnvelope = {
  data?: unknown;
  errors?: unknown;
  items?: unknown;
  records?: unknown;
  logs?: unknown;
  applicationErrors?: unknown;
  totalRows?: unknown;
  totalPages?: unknown;
  currentPage?: unknown;
  totalCount?: unknown;
  count?: unknown;
  totalLogs?: unknown;
  totalCriticalLogs?: unknown;
  totalErrorLogs?: unknown;
  totalInfoLogs?: unknown;
  summary?: unknown;
  counts?: unknown;
};

type ApplicationErrorSummaryResponseEnvelope = {
  totalLogs?: unknown;
  criticalLogs?: unknown;
  errorLogs?: unknown;
  warnLogs?: unknown;
  infoLogs?: unknown;
  totalCriticalLogs?: unknown;
  totalErrorLogs?: unknown;
  totalWarnLogs?: unknown;
  totalInfoLogs?: unknown;
  summary?: unknown;
  counts?: unknown;
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const isApplicationErrorListItemArray = (value: unknown): value is ApplicationErrorListItem[] => Array.isArray(value);

const extractApplicationErrorRows = (responseData: unknown): ApplicationErrorListItem[] => {
  if (isApplicationErrorListItemArray(responseData)) return responseData;
  if (!isRecord(responseData)) return [];

  const envelope = responseData as ApplicationErrorListResponseEnvelope;
  const candidateKeys = ['data', 'errors', 'items', 'records', 'logs', 'applicationErrors'] as const;

  for (const key of candidateKeys) {
    const candidate = envelope[key];
    if (isApplicationErrorListItemArray(candidate)) return candidate;
    if (isRecord(candidate) && isApplicationErrorListItemArray(candidate.data)) {
      return candidate.data;
    }
  }

  return [];
};

const normalizeApplicationErrorListResponse = (
  responseData: unknown,
  params?: ApplicationErrorListQueryParams
): ApplicationErrorListResponse => {
  const data = extractApplicationErrorRows(responseData);
  const responseEnvelope = isRecord(responseData) ? (responseData as ApplicationErrorListResponseEnvelope) : {};
  const totalRowsRaw =
    responseEnvelope.totalRows ?? responseEnvelope.totalCount ?? responseEnvelope.count ?? data.length;
  const totalRows = typeof totalRowsRaw === 'number' ? totalRowsRaw : data.length;
  const totalPages =
    typeof responseEnvelope.totalPages === 'number'
      ? responseEnvelope.totalPages
      : params?.pageSize
        ? Math.max(1, Math.ceil(totalRows / params.pageSize))
        : 1;
  const currentPage =
    typeof responseEnvelope.currentPage === 'number' ? responseEnvelope.currentPage : (params?.pageNo ?? 1);
  const summary = isRecord(responseEnvelope.summary)
    ? (responseEnvelope.summary)
    : isRecord(responseEnvelope.counts)
      ? (responseEnvelope.counts)
      : {};

  const totalLogsRaw = responseEnvelope.totalLogs ?? totalRowsRaw ?? data.length;
  const totalCriticalLogsRaw =
    responseEnvelope.totalCriticalLogs ?? summary.totalCriticalLogs ?? summary.critical ?? summary.CRITICAL;
  const totalErrorLogsRaw = responseEnvelope.totalErrorLogs ?? summary.totalErrorLogs ?? summary.error ?? summary.ERROR;
  const totalInfoLogsRaw = responseEnvelope.totalInfoLogs ?? summary.totalInfoLogs ?? summary.info ?? summary.INFO;

  const totalLogs = typeof totalLogsRaw === 'number' ? totalLogsRaw : totalRows;
  const totalCriticalLogs =
    typeof totalCriticalLogsRaw === 'number'
      ? totalCriticalLogsRaw
      : data.filter((row) => row.severity === 'CRITICAL').length;
  const totalErrorLogs =
    typeof totalErrorLogsRaw === 'number' ? totalErrorLogsRaw : data.filter((row) => row.severity === 'ERROR').length;
  const totalInfoLogs =
    typeof totalInfoLogsRaw === 'number' ? totalInfoLogsRaw : data.filter((row) => row.severity === 'INFO').length;

  return {
    data,
    totalRows,
    totalPages,
    currentPage,
    totalLogs,
    totalCriticalLogs,
    totalErrorLogs,
    totalInfoLogs,
  };
};

const normalizeApplicationErrorSummaryResponse = (responseData: unknown): ApplicationErrorSummaryResponse => {
  const responseEnvelope = isRecord(responseData) ? (responseData as ApplicationErrorSummaryResponseEnvelope) : {};
  const summary = isRecord(responseEnvelope.summary)
    ? (responseEnvelope.summary)
    : isRecord(responseEnvelope.counts)
      ? (responseEnvelope.counts)
      : {};

  const totalLogsRaw = responseEnvelope.totalLogs ?? summary.totalLogs ?? summary.total ?? 0;
  const criticalLogsRaw =
    responseEnvelope.criticalLogs ??
    responseEnvelope.totalCriticalLogs ??
    summary.criticalLogs ??
    summary.critical ??
    0;
  const errorLogsRaw =
    responseEnvelope.errorLogs ?? responseEnvelope.totalErrorLogs ?? summary.errorLogs ?? summary.error ?? 0;
  const warnLogsRaw =
    responseEnvelope.warnLogs ??
    responseEnvelope.infoLogs ??
    responseEnvelope.totalWarnLogs ??
    responseEnvelope.totalInfoLogs ??
    summary.warnLogs ??
    summary.infoLogs ??
    summary.warn ??
    summary.info ??
    0;

  return {
    totalLogs: typeof totalLogsRaw === 'number' ? totalLogsRaw : 0,
    criticalLogs: typeof criticalLogsRaw === 'number' ? criticalLogsRaw : 0,
    errorLogs: typeof errorLogsRaw === 'number' ? errorLogsRaw : 0,
    warnLogs: typeof warnLogsRaw === 'number' ? warnLogsRaw : 0,
  };
};

const getApplicationErrors = async (params?: ApplicationErrorListQueryParams) => {
  const response = await axiosInstance.get<unknown>('/application-errors', {
    params,
  });
  return normalizeApplicationErrorListResponse(response.data, params);
};

const getApplicationErrorSummary = async () => {
  const response = await axiosInstance.get<unknown>('/application-errors/summary');
  return normalizeApplicationErrorSummaryResponse(response.data);
};

export const useGetApplicationErrors = (
  params?: ApplicationErrorListQueryParams,
  options?: Omit<UseQueryOptions<ApplicationErrorListResponse>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: [
      'application-errors',
      params?.severity ?? 'ALL',
      params?.search ?? '',
      params?.pageNo ?? 1,
      params?.pageSize ?? 10,
    ],
    queryFn: () => getApplicationErrors(params),
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useGetApplicationErrorSummary = (
  options?: Omit<UseQueryOptions<ApplicationErrorSummaryResponse>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: ['application-errors-summary'],
    queryFn: getApplicationErrorSummary,
    refetchOnWindowFocus: false,
    ...options,
  });
};

const getApplicationErrorById = async (errorId: string) => {
  const response = await axiosInstance.get<ApplicationErrorDetails>(`/application-errors/${errorId}`);
  return response.data;
};

export const useGetApplicationErrorById = (
  errorId: string | null,
  options?: Omit<UseQueryOptions<ApplicationErrorDetails>, 'queryKey' | 'queryFn' | 'enabled'>
) => {
  return useQuery({
    queryKey: ['application-error', errorId],
    queryFn: () => {
      if (!errorId) throw new Error('errorId is required');
      return getApplicationErrorById(errorId);
    },
    enabled: !!errorId,
    refetchOnWindowFocus: false,
    ...options,
  });
};
